import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  FileSpreadsheet, Link2, Unlink, RefreshCw, CheckCircle, 
  Users, Target, Mail, Settings, Trash2, ArrowRight, Database, Clock, Play, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { api } from '../utils/api';

export default function SheetsSync() {
  const [searchParams] = useSearchParams();
  const [sheetsStatus, setSheetsStatus] = useState({ is_configured: false, is_connected: false });
  const [isConnecting, setIsConnecting] = useState(false);
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [sheetName, setSheetName] = useState('Residential');
  const [tabGid, setTabGid] = useState('0');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [autoMatch, setAutoMatch] = useState(true);
  const [emailResults, setEmailResults] = useState(true);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [syncInterval, setSyncInterval] = useState(60);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [cronRuns, setCronRuns] = useState([]);

  const fetchHistory = async () => {
    try {
      const [logsRes, runsRes] = await Promise.all([
        api.get('/sheets/sync-logs?limit=15'),
        api.get('/cron/sheets-sync/runs?limit=5'),
      ]);
      setSyncLogs(logsRes.data.logs || []);
      setCronRuns(runsRes.data.runs || []);
    } catch (error) {
      console.error('Failed to fetch sync history:', error);
    }
  };

  const handleToggleConfig = async (config, updates) => {
    try {
      await api.patch('/sheets/configs/toggle', {
        spreadsheet_id: config.spreadsheet_id,
        tab_gid: config.tab_gid || '0',
        ...updates,
      });
      toast.success(updates.disabled ? 'Sheet disabled (leads kept)' : 'Sheet configuration updated');
      fetchSheetsStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update configuration');
    }
  };

  useEffect(() => {
    fetchSheetsStatus();
    fetchHistory();
    
    if (searchParams.get('connected') === 'true') {
      toast.success('Google Sheets connected successfully!');
      fetchSheetsStatus();
    }
  }, [searchParams]);

  const fetchSheetsStatus = async () => {
    try {
      const response = await api.get('/sheets/status');
      setSheetsStatus(response.data);
      setSavedConfigs(response.data.saved_sheets || []);
    } catch (error) {
      console.error('Failed to fetch sheets status:', error);
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const response = await api.get('/sheets/auth-url');
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start connection');
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Sheets?')) return;
    
    try {
      await api.delete('/sheets/disconnect');
      toast.success('Google Sheets disconnected');
      setSheetsStatus({ ...sheetsStatus, is_connected: false });
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSync = async () => {
    if (!spreadsheetId) {
      toast.error('Please enter a Spreadsheet URL');
      return;
    }
    
    setIsSyncing(true);
    setSyncResult(null);
    try {
      const response = await api.post('/sheets/sync-public', {
        spreadsheet_url: spreadsheetId,
        sheet_name: sheetName,
        tab_gid: tabGid,
        auto_match: autoMatch,
        email_results: emailResults,
        auto_sync_enabled: autoSyncEnabled,
        sync_interval_minutes: parseInt(syncInterval)
      });
      
      setSyncResult(response.data);
      toast.success(response.data.message);
      if (autoSyncEnabled) {
        toast.info('Auto-sync enabled! New leads will be imported automatically every 15 minutes.');
      }
      fetchSheetsStatus();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed. Make sure the sheet is shared as "Anyone with the link can view"');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleManualAutoSync = async () => {
    try {
      const response = await api.post('/sheets/auto-sync');
      toast.success(response.data.message);
      if (response.data.errors?.length) {
        toast.error(`${response.data.errors.length} sheet(s) failed: ${response.data.errors[0].error}`);
      }
      fetchSheetsStatus();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Auto-sync failed');
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Remove this saved sheet?')) return;
    
    try {
      await api.delete(`/sheets/configs/${configId}`);
      toast.success('Configuration removed');
      fetchSheetsStatus();
    } catch (error) {
      toast.error('Failed to remove configuration');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="sheets-sync-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileSpreadsheet className="w-7 h-7 text-green-500" />
            Google Sheets Lead Sync
          </h1>
          <p className="text-slate-400 mt-1">
            Import leads from Google Sheets, automatically find lender matches, and get email notifications
          </p>
        </div>
      </div>

      {/* Sync Configuration */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Sheet Input */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-gold" />
            Sync Shared Google Sheet
          </h2>
          
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4">
            <p className="text-green-400 text-sm">
              <CheckCircle className="w-4 h-4 inline mr-1" />
              No login required! Just paste the sheet URL if it's shared as "Anyone with the link can view"
            </p>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Google Sheet URL *</label>
              <input
                type="text"
                value={spreadsheetId}
                onChange={(e) => setSpreadsheetId(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder:text-slate-500 focus:border-gold outline-none"
                data-testid="spreadsheet-id-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Tab Name</label>
                <input
                  type="text"
                  value={sheetName}
                  onChange={(e) => setSheetName(e.target.value)}
                  placeholder="Residential"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder:text-slate-500 focus:border-gold outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Tab GID (0 for first tab)</label>
                <input
                  type="text"
                  value={tabGid}
                  onChange={(e) => setTabGid(e.target.value)}
                  placeholder="0"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder:text-slate-500 focus:border-gold outline-none"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoMatch}
                  onChange={(e) => setAutoMatch(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-gold focus:ring-gold"
                />
                <span className="text-slate-300 text-sm">Auto-find lender matches for new leads</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailResults}
                  onChange={(e) => setEmailResults(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-gold focus:ring-gold"
                />
                <span className="text-slate-300 text-sm">Email me sync results with matches</span>
              </label>
            </div>
            
            {/* Auto-Sync Section */}
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoSyncEnabled}
                  onChange={(e) => setAutoSyncEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-green-500 focus:ring-green-500"
                />
                <span className="text-white font-medium text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4 text-green-400" />
                  Enable Auto-Sync (Check for new leads automatically)
                </span>
              </label>
              
              {autoSyncEnabled && (
                <div className="mt-3 ml-6">
                  <label className="text-sm text-slate-400 mb-1 block">Check every (minutes)</label>
                  <select
                    value={syncInterval}
                    onChange={(e) => setSyncInterval(e.target.value)}
                    className="bg-slate-800 border border-slate-600 rounded px-3 py-1 text-white text-sm"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="120">2 hours</option>
                    <option value="360">6 hours</option>
                    <option value="1440">Daily</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">New leads will be imported and matched automatically</p>
                </div>
              )}
            </div>
            
            <Button
              onClick={handleSync}
              disabled={isSyncing || !spreadsheetId}
              className="w-full bg-green-600 hover:bg-green-500 text-white font-semibold py-3"
              data-testid="sync-sheet-btn"
            >
              {isSyncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Syncing & Finding Matches...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {autoSyncEnabled ? 'Save & Sync Now' : 'Sync Now'}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-green-500" />
            {syncResult ? 'Sync Results' : 'How It Works'}
          </h2>
          
          {syncResult ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex-1 text-center">
                  <Users className="w-6 h-6 text-blue-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-white">{syncResult.new_leads_count}</p>
                  <p className="text-sm text-slate-400">New Leads</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex-1 text-center">
                  <Target className="w-6 h-6 text-green-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-white">{syncResult.matches_found}</p>
                  <p className="text-sm text-slate-400">Matches Found</p>
                </div>
              </div>
              
              {syncResult.new_leads?.length > 0 && (
                <div>
                  <p className="text-sm text-slate-400 mb-2">Imported Leads:</p>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {syncResult.new_leads.slice(0, 5).map((lead, i) => (
                      <div key={i} className="bg-slate-900/50 rounded-lg p-2 text-sm">
                        <span className="text-white font-medium">{lead.first_name} {lead.last_name}</span>
                        <span className="text-slate-500 ml-2">{lead.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {syncResult.matches?.length > 0 && (
                <div>
                  <p className="text-sm text-slate-400 mb-2">Lender Matches:</p>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {syncResult.matches.slice(0, 5).map((match, i) => (
                      <div key={i} className="bg-green-500/10 border border-green-500/20 rounded-lg p-2 text-sm">
                        <span className="text-white">{match.lead_name}</span>
                        <ArrowRight className="w-3 h-3 inline mx-2 text-slate-500" />
                        <span className="text-green-400">{match.lender_name} ({match.match_score}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {emailResults && (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Mail className="w-4 h-4" />
                  <span>Email sent with full results</span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h3 className="text-white font-medium mb-3">Auto-detected columns:</h3>
                <ul className="text-slate-400 text-sm space-y-1">
                  <li>• First Name, Last Name</li>
                  <li>• Email, Phone</li>
                  <li>• Reason for Capital, Amount Needed</li>
                  <li>• Property Type, FICO Range</li>
                </ul>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-blue-400 text-sm">
                  <strong>Tip:</strong> Every run scans the whole tab and skips any row whose email or phone already exists in the CRM, so repeated syncs never create duplicates and never overwrite your notes, statuses or assignments.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Saved Configurations */}
      {savedConfigs.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Saved Sheets</h2>
            <Button
              size="sm"
              variant="outline"
              onClick={handleManualAutoSync}
              className="text-green-400 border-green-600 hover:bg-green-500/10"
            >
              <Play className="w-4 h-4 mr-2" />
              Run Auto-Sync Now
            </Button>
          </div>
          <div className="space-y-2">
            {savedConfigs.map((config) => (
              <div
                key={config.config_id || `${config.spreadsheet_id}-${config.tab_gid}`}
                className={`flex flex-col md:flex-row md:items-center justify-between gap-3 rounded-lg p-3 ${config.disabled ? 'bg-slate-900/30 opacity-60' : 'bg-slate-900/50'}`}
                data-testid={`sheet-config-${config.tab_gid || '0'}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white text-sm font-medium truncate max-w-md">
                      {config.sheet_name || 'Sheet1'}
                      <span className="text-slate-500 font-normal ml-2">GID {config.tab_gid || '0'}</span>
                    </p>
                    {config.disabled ? (
                      <span className="bg-slate-700 text-slate-300 text-xs px-2 py-0.5 rounded-full" data-testid={`sheet-config-${config.tab_gid || '0'}-disabled-badge`}>Disabled</span>
                    ) : config.auto_sync_enabled ? (
                      <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Auto-sync every {config.sync_interval_minutes || 60} min
                      </span>
                    ) : (
                      <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full">Manual only</span>
                    )}
                    {config.last_error && (
                      <span className="bg-red-500/20 text-red-400 text-xs px-2 py-0.5 rounded-full flex items-center gap-1" title={config.last_error} data-testid={`sheet-config-${config.tab_gid || '0'}-error-badge`}>
                        <AlertTriangle className="w-3 h-3" />
                        {config.consecutive_failures || 1} failure{(config.consecutive_failures || 1) > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-slate-500 text-xs mt-1" data-testid={`sheet-config-${config.tab_gid || '0'}-status`}>
                    {config.total_rows_in_sheet ?? config.last_synced_row ?? 0} rows in sheet
                    {config.total_leads_imported != null && ` | ${config.total_leads_imported} leads imported`}
                    {config.last_success_at
                      ? ` | Last successful sync: ${new Date(config.last_success_at).toLocaleString()}`
                      : config.last_auto_sync ? ` | Last: ${new Date(config.last_auto_sync).toLocaleString()}` : ' | Never synced successfully'}
                    {config.last_result && ` (${config.last_result.new_leads} new, ${config.last_result.skipped_duplicates} duplicates skipped)`}
                  </p>
                  {config.last_error && (
                    <p className="text-red-400/80 text-xs mt-1 truncate" title={config.last_error}>
                      Last error: {config.last_error}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggleConfig(config, config.disabled ? { disabled: false, auto_sync_enabled: true } : { disabled: true })}
                    className={config.disabled ? 'text-green-400 border-green-600' : 'text-amber-400 border-amber-600'}
                    data-testid={`sheet-config-${config.tab_gid || '0'}-toggle-btn`}
                  >
                    {config.disabled ? 'Enable' : 'Disable'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSpreadsheetId(config.spreadsheet_url || config.spreadsheet_id);
                      setSheetName(config.sheet_name || 'Sheet1');
                      setTabGid(config.tab_gid || '0');
                      setAutoSyncEnabled(config.auto_sync_enabled || false);
                      setSyncInterval(config.sync_interval_minutes || 60);
                    }}
                    className="text-slate-300 border-slate-600"
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteConfig(config.config_id || config.spreadsheet_id)}
                    className="text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scheduler + Sync History */}
      <div className="grid lg:grid-cols-3 gap-6 min-w-0">
        <div className="min-w-0 bg-slate-800/50 border border-slate-700 rounded-xl p-6" data-testid="scheduler-status-panel">
          <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gold" />
            Scheduler (every 15 min)
          </h2>
          {cronRuns.length === 0 ? (
            <p className="text-slate-500 text-sm">No scheduled runs recorded yet.</p>
          ) : (
            <ul className="space-y-2">
              {cronRuns.map((run) => (
                <li key={run.run_id} className="text-xs bg-slate-900/50 rounded-lg p-2 flex items-center justify-between gap-2">
                  <span className="text-slate-400">{new Date(run.received_at).toLocaleString()}</span>
                  <span className={run.status === 'success' ? 'text-green-400' : run.status === 'error' ? 'text-red-400' : 'text-amber-400'}>
                    {run.status}{run.status === 'success' && ` · ${run.new_leads_total ?? 0} new`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="min-w-0 lg:col-span-2 bg-slate-800/50 border border-slate-700 rounded-xl p-6" data-testid="sync-history-panel">
          <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Database className="w-5 h-5 text-gold" />
            Sync History
          </h2>
          {syncLogs.length === 0 ? (
            <p className="text-slate-500 text-sm">No sync runs yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500 text-left">
                  <tr>
                    <th className="pb-2 pr-3">When</th>
                    <th className="pb-2 pr-3">Tab</th>
                    <th className="pb-2 pr-3">Trigger</th>
                    <th className="pb-2 pr-3">Rows</th>
                    <th className="pb-2 pr-3">New</th>
                    <th className="pb-2 pr-3">Dupes</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {syncLogs.map((log) => (
                    <tr key={log.log_id} className="border-t border-slate-700/60 text-slate-300" data-testid={`sync-log-${log.log_id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{new Date(log.started_at).toLocaleString()}</td>
                      <td className="py-2 pr-3">{log.sheet_name}</td>
                      <td className="py-2 pr-3 capitalize">{log.trigger}</td>
                      <td className="py-2 pr-3">{log.rows_scanned ?? '-'}</td>
                      <td className="py-2 pr-3 text-green-400">{log.new_leads ?? '-'}</td>
                      <td className="py-2 pr-3">{log.skipped_duplicates ?? '-'}</td>
                      <td className="py-2">
                        {log.status === 'success'
                          ? <span className="text-green-400">OK</span>
                          : <span className="text-red-400" title={log.error}>Error</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
